/**
 * `clauses` and `clause-detail` — FR-16, FR-17 · CLM-55 (G5, approved 9 Sep 2026).
 *
 * The **read side of the clause library**. `manage-clause` creates, revises and
 * retires; clause search (`POST /v1/search target=clauses`) ranks against a `q`
 * and cannot *list*. Until these two endpoints nothing let Legal see what had
 * been published, so the surface had nothing to bind.
 *
 * ## Corpus-level, and gated `contract:read`
 *
 * `clm.clause_library` carries no party and no contract column, and FR-17 grants
 * read of the library to every authorised user — *"all authorised may read and
 * reuse; only reviewers may edit master text."* So the list is unscoped and
 * `page.total_in_scope` is `null`, exactly as `clause-candidates` reasons: a
 * corpus total under a name that promises a scoped figure would be the one thing
 * `PageInfoSchema` says it must never be.
 *
 * The one scoped number is `in_scope_usage_count` on the detail: a count of
 * **contracts** that reference the clause, anchored on `root_contract_key`, so a
 * reviewer with a practice scope sees only the contracts they may read. The
 * field name says which count it is.
 *
 * ## Version history is rows, not a diff
 *
 * Migration 0021 stores one row per version — `version_no`, `is_current`,
 * `supersedes_clause_id` — and 0083 adds `revision_note` (D-B9). The detail
 * returns that chain newest first, each with its `embedding_state`: a revised
 * clause has **no** `clause_embedding` rows until `embed-clauses` next runs
 * (0021's structural staleness), and the surface shows that rather than hiding
 * it.
 */
import { z } from 'zod';
import { ClauseCodeSchema, IsoTimestampUtcSchema, OpaqueIdSchema, UpnSchema, VersionNoSchema } from '../primitives.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { LibraryClauseSchema } from '../domain/clause.js';
/* -------------------------------------------------------------------------- */
/* GET /v1/clauses                                                             */
/* -------------------------------------------------------------------------- */
export const ClauseListRequestSchema = z
    .object({
    /** Retired clauses are hidden unless asked for. Server default: `false`. */
    include_inactive: z.boolean().optional(),
    category: z.string().min(1).max(60).optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
/** A row of the list is exactly the domain clause — no extra columns. */
export const ClauseListItemSchema = LibraryClauseSchema;
export const ClauseListResponseSchema = z.object({
    items: z.array(ClauseListItemSchema),
    page: PageInfoSchema,
    /** Corpus-level counters for the header, like `clause-candidates.totals`. */
    totals: z.object({
        current: z.number().int().min(0),
        active: z.number().int().min(0),
        /**
         * Current clauses whose WP-09 §4 representation 3 was never generated —
         * `ix_clause_library_purpose_missing` (0021). A real admin signal: those
         * clauses are retrievable on two representations instead of three.
         */
        purpose_missing: z.number().int().min(0),
    }),
});
/* -------------------------------------------------------------------------- */
/* GET /v1/clauses/{clause_code}                                               */
/* -------------------------------------------------------------------------- */
export const ClauseDetailRequestSchema = z
    .object({
    /** Path parameter. `clause_code` is how a clause is addressed everywhere. */
    clause_code: ClauseCodeSchema,
})
    .strict();
/**
 * Whether this version's three WP-09 §4 vectors exist yet.
 *
 * `pending` is not a failure: a freshly created or revised version has no
 * `clause_embedding` rows by design (K5 — no second vector write path), and the
 * next `embed-clauses` run fills them.
 */
export const CLAUSE_EMBEDDING_STATES = ['embedded', 'pending'];
export const ClauseEmbeddingStateSchema = z.enum(CLAUSE_EMBEDDING_STATES);
/** One row of the version chain — every column is 0021's, plus 0083's note. */
export const ClauseVersionSchema = z.object({
    clause_id: OpaqueIdSchema,
    version_no: VersionNoSchema,
    is_current: z.boolean(),
    supersedes_clause_id: OpaqueIdSchema.nullable(),
    title: z.string().min(1).max(200),
    category: z.string().max(60).nullable(),
    master_text: z.string().min(1),
    created_by_upn: UpnSchema.nullable(),
    created_utc: IsoTimestampUtcSchema,
    last_edited_by_upn: UpnSchema.nullable(),
    last_edited_utc: IsoTimestampUtcSchema.nullable(),
    /** Why this version was written (FR-17). `null` on v1 and mining-published rows. */
    revision_note: z.string().max(1000).nullable(),
    embedding_state: ClauseEmbeddingStateSchema,
});
export const ClauseDetailResponseSchema = z.object({
    clause: LibraryClauseSchema,
    /** Newest first. Includes `is_current = false` predecessors — the FR-17 history. */
    versions: z.array(ClauseVersionSchema).min(1),
    /** Contracts referencing this clause **that the caller may read** (root_contract_key). */
    in_scope_usage_count: z.number().int().min(0),
});
//# sourceMappingURL=clauses.js.map